import React from 'react';
import { Box, Skeleton } from '@mui/material';
import { useInView } from '../hooks/useInView';

/**
 * Reveal component - Provides entrance animations with accessibility support
 * Only animates opacity and transform to avoid layout thrashing
 * Respects prefers-reduced-motion
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Content to reveal
 * @param {number} props.delay - Animation delay in ms (for staggering)
 * @param {string} props.animation - Animation type: 'fade', 'slideUp', 'slideLeft'
 * @param {boolean} props.showSkeleton - Whether to show skeleton while not in view
 * @param {number} props.skeletonHeight - Height of skeleton in px
 * @param {Object} props.sx - Additional sx props
 */
const Reveal = ({
  children,
  delay = 0,
  animation = 'fade',
  showSkeleton = false,
  skeletonHeight = 60,
  sx = {},
  ...props
}) => {
  const [ref, inView] = useInView({
    threshold: 0.1,
    rootMargin: '0px 0px -5% 0px',
    triggerOnce: true
  });

  // Animation configurations
  const animations = {
    fade: {
      initial: { opacity: 0 },
      animate: { opacity: 1 }
    },
    slideUp: {
      initial: { opacity: 0, transform: 'translateY(20px)' },
      animate: { opacity: 1, transform: 'translateY(0px)' }
    },
    slideLeft: {
      initial: { opacity: 0, transform: 'translateX(20px)' },
      animate: { opacity: 1, transform: 'translateX(0px)' }
    }
  };

  const { initial, animate } = animations[animation] || animations.fade;

  // Check for prefers-reduced-motion
  const prefersReducedMotion = typeof window !== 'undefined' 
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches 
    : false;

  const getTransitionStyle = () => {
    if (prefersReducedMotion) {
      return {
        opacity: 1,
        transform: 'none'
      };
    }

    return {
      ...(inView ? animate : initial),
      transition: `all 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${delay}ms`,
      willChange: inView ? 'auto' : 'opacity, transform'
    };
  };

  // Show skeleton if not in view and skeleton is enabled
  if (!inView && showSkeleton && !prefersReducedMotion) {
    return (
      <Box ref={ref} sx={sx} {...props}>
        <Skeleton
          variant="rectangular"
          height={skeletonHeight}
          sx={{
            borderRadius: 1,
            transform: 'scale(1)' // Prevent skeleton animation
          }}
        />
      </Box>
    );
  }

  return (
    <Box
      ref={ref}
      sx={{
        ...getTransitionStyle(),
        ...sx
      }}
      {...props}
    >
      {children}
    </Box>
  );
};

/**
 * RevealStagger component - Automatically staggers children reveals
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - Children to stagger
 * @param {number} props.staggerDelay - Delay between each child in ms
 * @param {string} props.animation - Animation type for all children
 */
export const RevealStagger = ({
  children,
  staggerDelay = 100,
  animation = 'slideUp',
  ...props
}) => {
  const childArray = React.Children.toArray(children);

  return (
    <Box {...props}>
      {childArray.map((child, index) => (
        <Reveal
          key={index}
          delay={index * staggerDelay}
          animation={animation}
        >
          {child}
        </Reveal>
      ))}
    </Box>
  );
};

export default Reveal;